import React, {useEffect} from 'react';
import { useParams } from 'react-router';
import PBIEmbedClassDriven from './PBIEmbedClassDriven';
import { useAuth0 } from '@auth0/auth0-react';
import { models } from 'powerbi-client';
import utils from '../utils';
import LeadershipListDisplay from './CompanyProfiles/LeadershipListDisplay';
import FilteredPagedNewsPage from './News/FilteredPagedNewsList';
import KeyEventsDisplay from './PartnerProfiles/KeyEventsDisplay';
import CompareCompTabs from './IndustryAnalysis/CompareCompTabs';
import ListSkeletonLoader from './Layout/ListSkeletonLoader';
import CountryNewsWrapper from './IRC/CountryNewsWrapper';
import VizUtils from '../VizUtils'
import LLMCodedField from './LLMCodedField';

import CONSTANTS from '../constants';

const HIDDEN_HEADER_FIELD_API_NAMES = new Set([
    'sourcesforintroduction'
]);

const HIDDEN_HEADER_TITLES = new Set([
    'Capability Analysis By Company',
    'Addressable Population Estimator'
]);

function shouldShowItemHeader(item) {
    const itemTitle = typeof item?.itemTitle === 'string' ? item.itemTitle.trim() : '';

    if (!itemTitle) {
        return false;
    }

    if (HIDDEN_HEADER_FIELD_API_NAMES.has(item.fieldAPIName)) {
        return false;
    }

    if (HIDDEN_HEADER_TITLES.has(itemTitle)) {
        return false;
    }

    return true;
}



function FieldDisplay({ fields, fieldsDescr, className=null, infoToShow=null, tenant=null, isForCountry=null, levelThreeNav=null, loading=false, worker=null}) {

    const DEFAULT_VIZ_HEIGHT = 500;


    let { infoID } = useParams();

    if (!infoID) {
        infoID = infoToShow;
    }

    const { getAccessTokenSilently, user } = useAuth0();
    

    // Initialize Google Analytics tracking
    useEffect(() => {
        utils.initializeTracking();
    }, []);




    // Track page views with Google Analytics
    // post a message to the web worker with the data for the page view to load up collection in group
    useEffect(() => {
        if (infoID) {
            utils.gaPageView(infoID);
        }

        if(worker) {
            //have to do this here because we need to access localStorage in the worker and can only access in main thread
            worker.onmessage = (event) => {
                if (event.data.type === CONSTANTS.WORKER_MESSAGE_TYPES.SAVE_TO_STORAGE) {
                    //save the item to localStorage - keyed by item.pbiReportID + '-' + item.pBIDatasetID

                    
                    if (event.data.idToUse && event.data.pbisToWrite) {
                        localStorage.setItem(event.data.idToUse, JSON.stringify(event.data.pbisToWrite));
                    } 
                }

                if (event.data.type === CONSTANTS.WORKER_MESSAGE_TYPES.GET_ITEM_IN_STORAGE) {
                    
                    let itemToGet = event.data.itemToGet;                    ;
                    let storedObj = localStorage.getItem(itemToGet);

                    utils.getAccessToken(getAccessTokenSilently, user).then((token) => {
                        worker.postMessage({
                            type: CONSTANTS.WORKER_MESSAGE_TYPES.ITEM_IN_STORAGE,
                            itemData: storedObj,
                            itemToGet: itemToGet,
                            pbiReportID: event.data.pbiReport,
                            pBIDatasetID: event.data.pbiDataset,
                            accessToken: token,
                            tenID: tenant && tenant.tenant && tenant.tenant.fields ? tenant.tenant.fields.aDGroupID : null
                        });
    
                    }).catch((err) => {

                    })
                    
                }
            }

            worker.postMessage({
                type: CONSTANTS.WORKER_MESSAGE_TYPES.NAV_DATA,
                navData: VizUtils.filterToUsedNavs(fieldsDescr, infoID)
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [infoID]);


    let fieldDescriptionItems = VizUtils.getDescriptionItem(fieldsDescr, infoID);
    if (fieldDescriptionItems) {
        fieldDescriptionItems.pageItems = VizUtils.filterOutPageItemsThatHaveNoContent(VizUtils.sortPageItemsByOrderDisplayed(fieldDescriptionItems.pageItems), fields.fields);
    }

    return (
        fieldDescriptionItems && fields && !loading ?
            <>
                {
                    fieldDescriptionItems.pageItems.map((thisItem, index) => {
                        const showItemHeader = shouldShowItemHeader(thisItem);

                        return (
                            <div className={`company-field-display ${className}`} key={index}>
                                {
                                    showItemHeader ?
                                        <h3>{thisItem.itemTitle}</h3>
                                        : null
                                }
                                {

                                        thisItem.fieldType === 'PBI' ?


                                            <PBIEmbedClassDriven className={`field-display-pbi ${thisItem.fieldAPIName} ${thisItem.pBISize ? thisItem.pBISize : 'pbiStandard'}`}
                                            //getOptionsForPBI returns the reportID and the datasetID  
                                            {...VizUtils.getOptionsForPBI(thisItem, fields, tenant, DEFAULT_VIZ_HEIGHT, models)} />

                                            :

                                            thisItem.fieldAPIName === 'showNewsApp' ?
                                                <CountryNewsWrapper 
                                                cntryName={isForCountry.country} />
                                                :

                                                thisItem.fieldAPIName === 'companyLeadershipProfiles' || thisItem.fieldAPIName === 'executives' ?
                                                    <LeadershipListDisplay
                                                        company={fields}
                                                        fieldName={thisItem.fieldAPIName} />
                                                    :

                                                    levelThreeNav && levelThreeNav.contentID ?

                                                        <CompareCompTabs
                                                            wholeEntity={fields}
                                                            analysisRec={levelThreeNav}
                                                            tenant={tenant} />
                                                        :

                                                        thisItem.fieldAPIName === 'keyEvents' ?
                                                            <KeyEventsDisplay entityToUse={fields} />

                                                            :

                                                            thisItem.fieldAPIName === 'news' ?
                                                                <FilteredPagedNewsPage
                                                                    query={VizUtils.convertNewsListToString(fields.fields.tags)}
                                                                    limitFields={true} />
                                                                :

                                                                thisItem.fieldType?.toLowerCase() === 'html' ?
                                                                    <LLMCodedField html={fields.fields[thisItem.fieldAPIName]} />
                                                                    :
                                                                    <article dangerouslySetInnerHTML={utils.getHTML(fields.fields[thisItem.fieldAPIName])} />
                                }
                            </div>
                        )
                    })
                }

            </>
            : <ListSkeletonLoader preview={false} />
    )
}

export default FieldDisplay
